import { useCallback, useEffect } from 'react'
import { FiUpload } from 'react-icons/fi'

import { useQuery } from '@tanstack/react-query'
import { t } from 'i18next'

import { Button } from '~/components/Buttons'
import Panel from '~/components/Panel'
import { history } from '~/helpers'
import useQueryParams from '~/hooks/useQuery'
import { useSidePanel } from '~/hooks/useSidePanel'
import { telemetryClient } from '~/lib/telemetry'
import {
  externalCampaignMetricsKey,
  ExternalCampaignMetricsService
} from '~/modules/retailMedia/services/metrics'

import ExportSection from './components/ExportSection'
import UploadsTable from './components/UploadsTable'
import ExternalCampaignMetricsForm from './form'

const ExternalCampaignMetricsPage = () => {
  const { addSidePanel, removeSidePanel } = useSidePanel()
  const params = useQueryParams()
  const page = Number(params.get('page')) || 1
  const quantity = Number(params.get('quantity')) || 50

  const {
    data,
    error,
    isPending: loading
  } = useQuery({
    queryKey: [`${externalCampaignMetricsKey}-list`, page, quantity],
    queryFn: async () => {
      const response = await ExternalCampaignMetricsService.list({
        page,
        quantity
      })

      return response.data
    }
  })

  const handleOpenForm = useCallback(() => {
    addSidePanel({
      title: t('rm:externalCampaignMetrics.modal.title'),
      type: 'content',
      size: 'fluid',
      content: (
        <ExternalCampaignMetricsForm onSuccess={() => removeSidePanel()} />
      ),
      callbackRemove: () => removeSidePanel()
    })
  }, [addSidePanel, removeSidePanel])

  const handlePagination = useCallback(
    (value: number) => {
      params.set('page', String(value))
      history.push({ search: params.toString() })
    },
    [params]
  )

  const handleItemsPerPageChange = useCallback(
    (value: { value: number }) => {
      params.set('quantity', String(value.value))
      params.set('page', '1')
      history.push({ search: params.toString() })
    },
    [params]
  )

  useEffect(() => {
    telemetryClient.logInfo({
      message: 'View: ExternalCampaignMetricsPage'
    })
  }, [])

  return (
    <Panel>
      <ExportSection />

      <div className="row">
        <UploadsTable
          newButton={
            <Button
              template="primary"
              text={t('rm:externalCampaignMetrics.button.import')}
              iconLeft={<FiUpload />}
              onClick={handleOpenForm}
            />
          }
          data={data}
          error={error}
          loading={loading}
          page={page}
          quantity={quantity}
          onPagination={handlePagination}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>
    </Panel>
  )
}

export default ExternalCampaignMetricsPage
